import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { toApiError, type ApiError } from '../../api/client';
import { ownerPropertyApi } from '../../api/endpoints';
import type { OwnerPropertyListItem, PagedResult } from '../../api/types';
import { ConfirmDialog, PageHeader, Pagination } from '../../components/Common';
import { IconHome } from '../../components/Icons';
import { EmptyState, ErrorState, InlineError, InlineSuccess, TableSkeleton } from '../../components/States';
import { availabilityLabel, categoryLabel, date, money, statusLabel } from '../../utils/format';

const STATUSES = [
  'Draft',
  'PendingPayment',
  'PendingReview',
  'Active',
  'ExpiringSoon',
  'Expired',
  'Rejected',
  'Suspended',
];

export function OwnerListingsPage() {
  const { isOwner, canManageHomeRentals } = useAuth();
  const [params, setParams] = useSearchParams();
  const mayCreateListing = isOwner || canManageHomeRentals;
  const createListingPath = isOwner
    ? '/owner/listings/new'
    : '/owner/listings/new?category=HomeRental';

  const [data, setData] = useState<PagedResult<OwnerPropertyListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<OwnerPropertyListItem | null>(null);
  const [busy, setBusy] = useState(false);

  const status = params.get('status') ?? undefined;
  const category = params.get('category') ?? undefined;
  const page = Number(params.get('page') ?? 1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await ownerPropertyApi.list({ status, category, page, pageSize: 20 }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status, category, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next);
  };

  const archive = async () => {
    if (!archiving) return;

    setBusy(true);

    try {
      await ownerPropertyApi.archive(archiving.id);
      setSuccess(`${archiving.title} has been archived and is no longer visible to customers.`);
      setArchiving(null);
      await load();
    } catch (err) {
      setError(toApiError(err));
      setArchiving(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Branches &amp; listings"
        description="Every property you operate, and where each one is in the publication flow."
        actions={mayCreateListing ? (
          <Link to={createListingPath} className="btn btn-primary btn-sm">
            {isOwner ? 'Add a property' : 'Add a HomeRental listing'}
          </Link>
        ) : undefined}
      />

      <InlineError message={error} />
      <InlineSuccess message={success} />

      <div className="sf-card sf-card-pad mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-sm-5">
            <label className="form-label small mb-1" htmlFor="filter-category">
              Category
            </label>
            <select
              id="filter-category"
              className="form-select form-select-sm"
              value={category ?? ''}
              onChange={(e) => setFilter('category', e.target.value || undefined)}
            >
              <option value="">All categories</option>
              <option value="PgHostel">PG &amp; Hostels</option>
              <option value="HomeRental">Homes for rent</option>
              <option value="Hotel">Hotels</option>
            </select>
          </div>

          <div className="col-12 col-sm-5">
            <label className="form-label small mb-1" htmlFor="filter-status">
              Status
            </label>
            <select
              id="filter-status"
              className="form-select form-select-sm"
              value={status ?? ''}
              onChange={(e) => setFilter('status', e.target.value || undefined)}
            >
              <option value="">All statuses</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value as never).text}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100"
              onClick={() => setParams(new URLSearchParams())}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {loading && <TableSkeleton rows={5} columns={5} />}
      {!loading && error && <ErrorState error={error} onRetry={load} />}

      {!loading && !error && data && data.items.length === 0 && (
        <EmptyState
          icon={<IconHome />}
          title="No listings here"
          description={
            status || category
              ? 'Nothing matches these filters.'
              : 'Add your first branch or property to get started.'
          }
          action={mayCreateListing ? (
            <Link to={createListingPath} className="btn btn-primary">
              {isOwner ? 'Add a property' : 'Add a HomeRental listing'}
            </Link>
          ) : undefined}
        />
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <>
          <div className="d-flex flex-column gap-3">
            {data.items.map((item) => (
              <ListingRow key={item.id} item={item} onArchive={() => setArchiving(item)} />
            ))}
          </div>

          <Pagination
            result={data}
            onPageChange={(next) => {
              const query = new URLSearchParams(params);
              query.set('page', String(next));
              setParams(query);
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={archiving !== null}
        title="Archive this listing?"
        destructive
        busy={busy}
        confirmLabel="Archive"
        message={
          <p className="mb-0">
            <strong>{archiving?.title}</strong> will be removed from search straight away. Its enquiries and payment
            history are kept, and you can restore it later.
          </p>
        }
        onConfirm={archive}
        onCancel={() => setArchiving(null)}
      />
    </>
  );
}

/**
 * The listing's standing against its category's payment rules, as the server decided it.
 * No price and no policy is inferred here.
 */
function BillingChip({ item }: { item: OwnerPropertyListItem }) {
  switch (item.billingRequirement) {
    case 'NotRequired':
      return <span className="sf-pill sf-pill--ok">Payment not required</span>;
    case 'Required':
      return <span className="sf-pill sf-pill--warn">Payment required</span>;
    case 'Pending':
      return <span className="sf-pill sf-pill--info">Payment started</span>;
    case 'Expired':
      return <span className="sf-pill sf-pill--warn">Paid period ended</span>;
    case 'Completed':
      return <span className="sf-pill sf-pill--ok">Paid</span>;
    default:
      return null;
  }
}

function ListingRow({ item, onArchive }: { item: OwnerPropertyListItem; onArchive: () => void }) {
  const status = statusLabel(item.status);

  const availability = availabilityLabel(
    item.availabilityConfidence,
    item.availableUnits,
    item.category === 'Hotel' ? 'rooms' : 'beds',
  );

  return (
    <div className="sf-card sf-card-pad">
      <div className="d-flex flex-wrap justify-content-between gap-3">
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <h3 className="h6 mb-0">{item.title}</h3>
            <span className={`badge bg-${status.variant}`}>{status.text}</span>
            <span className="sf-chip">{categoryLabel(item.category)}</span>
          </div>

          <p className="sf-muted small mb-2">
            {item.businessName} · {item.locality}
            {item.priceFrom !== undefined && item.priceFrom !== null && <> · from {money(item.priceFrom)}</>}
          </p>

          {item.statusReason && (
            <div className="alert alert-warning py-2 px-3 small mb-2" role="status">
              {item.statusReason}
            </div>
          )}

          <div className="d-flex flex-wrap gap-2 small">
            <span className="sf-chip">{item.photoCount} photo{item.photoCount === 1 ? '' : 's'}</span>

            {item.category !== 'HomeRental' && (
              <>
                <span className="sf-chip">{item.roomTypeCount} room type{item.roomTypeCount === 1 ? '' : 's'}</span>
                <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>
              </>
            )}

            {item.newEnquiryCount > 0 && (
              <span className="sf-pill sf-pill--info">{item.newEnquiryCount} new enquir{item.newEnquiryCount === 1 ? 'y' : 'ies'}</span>
            )}

            <span className="sf-chip">{item.viewCount} views</span>

            {item.expiresAtUtc && <span className="sf-chip">Expires {date(item.expiresAtUtc)}</span>}

            <BillingChip item={item} />
          </div>

          {item.subscription && (
            <p className="sf-muted small mt-2 mb-0">
              {item.subscription.planName} · {item.subscription.status}
              {item.subscription.daysRemaining !== undefined && <> · {item.subscription.daysRemaining} days left</>}
            </p>
          )}
        </div>

        <div className="d-flex flex-column gap-2">
          <Link to={`/owner/listings/${item.id}`} className="btn btn-sm btn-primary">
            Manage
          </Link>
          <Link to={`/owner/listings/${item.id}/edit`} className="btn btn-sm btn-outline-primary">
            Edit details
          </Link>
          {(item.status === 'Active' || item.status === 'ExpiringSoon') && (
            <Link to={`/listing/${item.id}`} className="btn btn-sm btn-outline-secondary">
              View public page
            </Link>
          )}
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={onArchive}>
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}
